export interface ChoiceBlockItem {
  id: string
  label: string
  markdown: string
}

export type ChoiceBlockMarkdownSegment =
  | {
      type: 'markdown'
      markdown: string
    }
  | {
      type: 'choiceBlock'
      choices: ChoiceBlockItem[]
    }

const CHOICE_BLOCK_PATTERN = /\[\[choices\]\]\n?([\s\S]*?)\n?\[\[\/choices\]\]/g
const CHOICE_ITEM_PATTERN = /\[\[choice\b([^\]]*)\]\]\n?([\s\S]*?)\n?\[\[\/choice\]\]/g
const CHOICE_BLOCK_HTML_PATTERN =
  /<choice-block\b([^>]*)>\s*<\/choice-block>|<choice-block\b([^>]*)\/>/g
const CHOICE_BLOCK_PLACEHOLDER = 'KARAKURI_CHOICE_BLOCK'

const createChoiceId = (index: number) => `choice-${index + 1}`

export const createDefaultChoiceBlockItems = (): ChoiceBlockItem[] => [
  { id: createChoiceId(0), label: '選択肢1', markdown: '' },
  { id: createChoiceId(1), label: '選択肢2', markdown: '' },
]

export function normalizeChoiceBlockItems(items: ChoiceBlockItem[]): ChoiceBlockItem[] {
  const normalizedItems = items
    .map((item, index) => ({
      id: item.id.trim() || createChoiceId(index),
      label: item.label.trim() || `選択肢${index + 1}`,
      markdown: trimBoundaryLineBreaks(item.markdown),
    }))
    .filter(item => item.label || item.markdown)

  return normalizedItems.length > 0 ? normalizedItems : createDefaultChoiceBlockItems()
}

export function encodeChoiceBlockItems(items: ChoiceBlockItem[]): string {
  return encodeURIComponent(JSON.stringify(normalizeChoiceBlockItems(items)))
}

export function decodeChoiceBlockItems(encodedItems: string | undefined): ChoiceBlockItem[] {
  if (!encodedItems) {
    return createDefaultChoiceBlockItems()
  }

  try {
    const parsed: unknown = JSON.parse(decodeURIComponent(encodedItems))
    if (!Array.isArray(parsed)) {
      return createDefaultChoiceBlockItems()
    }

    return normalizeChoiceBlockItems(
      parsed.filter(isChoiceBlockItemLike).map((item, index) => ({
        id: item.id ?? createChoiceId(index),
        label: item.label ?? `選択肢${index + 1}`,
        markdown: item.markdown ?? '',
      }))
    )
  } catch {
    return createDefaultChoiceBlockItems()
  }
}

export function serializeChoiceBlockMarkdown(items: ChoiceBlockItem[]): string {
  const lines = ['[[choices]]']
  normalizeChoiceBlockItems(items).forEach(item => {
    lines.push(`[[choice label="${escapeShortcodeAttribute(item.label)}"]]`)
    if (item.markdown) {
      lines.push(item.markdown)
    }
    lines.push('[[/choice]]')
  })
  lines.push('[[/choices]]')
  return lines.join('\n')
}

export function splitChoiceBlocksFromMarkdown(markdown: string): ChoiceBlockMarkdownSegment[] {
  const segments: ChoiceBlockMarkdownSegment[] = []
  let lastIndex = 0

  markdown.replace(CHOICE_BLOCK_PATTERN, (matchedText: string, body: string, offset: number) => {
    if (offset > lastIndex) {
      segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex, offset) })
    }
    segments.push({ type: 'choiceBlock', choices: parseChoiceBlockBody(body) })
    lastIndex = offset + matchedText.length
    return matchedText
  })

  if (lastIndex < markdown.length) {
    segments.push({ type: 'markdown', markdown: markdown.slice(lastIndex) })
  }

  return segments.length > 0 ? segments : [{ type: 'markdown', markdown }]
}

export function replaceChoiceBlockMarkdownWithPlaceholders(markdown: string): {
  markdown: string
  choices: ChoiceBlockItem[][]
} {
  const choices: ChoiceBlockItem[][] = []
  const replacedMarkdown = markdown.replace(CHOICE_BLOCK_PATTERN, (_matchedText, body: string) => {
    const index = choices.length
    choices.push(parseChoiceBlockBody(body))
    return `${CHOICE_BLOCK_PLACEHOLDER}_${index}`
  })

  return { markdown: replacedMarkdown, choices }
}

export function restoreChoiceBlockHtmlPlaceholders(
  html: string,
  choicesList: ChoiceBlockItem[][]
): string {
  return choicesList.reduce((restoredHtml, choices, index) => {
    const placeholder = `${CHOICE_BLOCK_PLACEHOLDER}_${index}`
    const choiceBlockHtml = createChoiceBlockHtml(choices)
    return restoredHtml
      .replace(new RegExp(`<p>\\s*${placeholder}\\s*</p>`, 'g'), choiceBlockHtml)
      .replace(new RegExp(placeholder, 'g'), choiceBlockHtml)
  }, html)
}

export function replaceChoiceBlockHtmlWithPlaceholders(html: string): {
  html: string
  choices: ChoiceBlockItem[][]
} {
  const choices: ChoiceBlockItem[][] = []
  const replacedHtml = html.replace(
    CHOICE_BLOCK_HTML_PATTERN,
    (_matchedText, attributesA: string | undefined, attributesB: string | undefined) => {
      const index = choices.length
      choices.push(decodeChoiceBlockItems(readHtmlAttribute(attributesA ?? attributesB ?? '')))
      return `<p>${CHOICE_BLOCK_PLACEHOLDER}_${index}</p>`
    }
  )

  return { html: replacedHtml, choices }
}

export function restoreChoiceBlockMarkdownPlaceholders(
  markdown: string,
  choicesList: ChoiceBlockItem[][]
): string {
  return choicesList.reduce(
    (restoredMarkdown, choices, index) =>
      restoredMarkdown.replace(
        createMarkdownPlaceholderRegExp(`${CHOICE_BLOCK_PLACEHOLDER}_${index}`),
        serializeChoiceBlockMarkdown(choices)
      ),
    markdown
  )
}

function parseChoiceBlockBody(body: string): ChoiceBlockItem[] {
  const items: ChoiceBlockItem[] = []

  body.replace(CHOICE_ITEM_PATTERN, (_matchedText, attributes: string, markdown: string) => {
    const label = readShortcodeAttribute(attributes, 'label') ?? `選択肢${items.length + 1}`
    items.push({
      id: createChoiceId(items.length),
      label,
      markdown: trimBoundaryLineBreaks(markdown),
    })
    return _matchedText
  })

  return normalizeChoiceBlockItems(items)
}

function createChoiceBlockHtml(items: ChoiceBlockItem[]): string {
  return `<choice-block data-choices="${escapeHtmlAttribute(
    encodeChoiceBlockItems(items)
  )}"></choice-block>`
}

function readShortcodeAttribute(attributes: string, name: string): string | undefined {
  const pattern = new RegExp(`${name}="((?:\\\\.|[^"\\\\])*)"`)
  const matched = pattern.exec(attributes)
  return matched ? unescapeShortcodeAttribute(matched[1]) : undefined
}

function readHtmlAttribute(attributes: string): string | undefined {
  const matched = /data-choices=(?:"([^"]*)"|'([^']*)'|([^\s>]+))/.exec(attributes)
  return matched?.[1] ?? matched?.[2] ?? matched?.[3]
}

function escapeShortcodeAttribute(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')
}

function unescapeShortcodeAttribute(value: string): string {
  return value.replace(/\\"/g, '"').replace(/\\\\/g, '\\')
}

function escapeHtmlAttribute(value: string): string {
  return value.replace(/&/g, '&amp;').replace(/"/g, '&quot;')
}

function createMarkdownPlaceholderRegExp(placeholder: string): RegExp {
  return new RegExp(escapeRegExp(placeholder).replace(/_/g, '\\\\?_'), 'g')
}

function escapeRegExp(value: string): string {
  return value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
}

function trimBoundaryLineBreaks(value: string): string {
  return value.replace(/^\n+|\n+$/g, '')
}

function isChoiceBlockItemLike(value: unknown): value is Partial<ChoiceBlockItem> {
  return typeof value === 'object' && value !== null
}
